import { useMemo } from 'react';
import { usePermissions } from '../../../context/PermissionsContext';

/**
 * The Wiki's three prototype roles, derived from runtime permissions
 * (REBUILD-PLAN §1 "Roles"):
 *   admin  ("Wiki admin") — runtime `admin` (wiki:admin) or any org admin
 *   editor ("Editor")     — runtime `manager` (wiki:write, + wiki:brain_view)
 *   member ("Member")     — runtime `user` / `sales_rep` (wiki:read)
 *
 * Capabilities are what the UI gates on; the API stays authoritative.
 *   canRead          read, Ask, acknowledge
 *   canEdit          create AND update pages, categories and subcategories
 *   canAdmin         category archive/delete + ACL, the Brain pending count
 *   canBrainView     see the AI Brain (read-only for editors)
 *   canBrainManage   decide proposals, change Brain settings
 *   canInvestigate   open Emotion-insight conversations
 *   loading          permissions not known yet — every capability is false
 */
export const WIKI_ROLE_LABELS = Object.freeze({
    admin: 'Wiki admin',
    editor: 'Editor',
    member: 'Member',
});

const LOADING_ROLE = Object.freeze({
    role: 'member',
    label: WIKI_ROLE_LABELS.member,
    canRead: false,
    canEdit: false,
    canAdmin: false,
    canBrainView: false,
    canBrainManage: false,
    canInvestigate: false,
    loading: true,
});

/** Pure mapping, exported for tests: `{ can, isOrgAdmin, permissionsUnknown }` → role. */
export function deriveWikiRole({ can, isOrgAdmin, permissionsUnknown } = {}) {
    if (permissionsUnknown || typeof can !== 'function') return LOADING_ROLE;
    const orgAdmin = typeof isOrgAdmin === 'function' && Boolean(isOrgAdmin());
    const canAdmin = orgAdmin || can('wiki:admin');
    const canEdit = canAdmin || can('wiki:write');
    const canRead = canEdit || can('wiki:read');
    const canBrainManage = canAdmin || can('wiki:brain_manage');
    const canBrainView = canBrainManage || can('wiki:brain_view');
    const canInvestigate = canAdmin || can('wiki:brain_investigate');
    const role = canAdmin ? 'admin' : (canEdit ? 'editor' : 'member');
    return {
        role,
        label: WIKI_ROLE_LABELS[role],
        canRead,
        canEdit,
        canAdmin,
        canBrainView,
        canBrainManage,
        canInvestigate,
        loading: false,
    };
}

export default function useWikiRole() {
    const { can, isOrgAdmin, permissionsUnknown } = usePermissions();
    return useMemo(
        () => deriveWikiRole({ can, isOrgAdmin, permissionsUnknown }),
        [can, isOrgAdmin, permissionsUnknown],
    );
}
